import type { PlaybookId } from "@repo/app-types/entities/playbook";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAllMinimalPlaybooksQuery } from "../../../api/entities/playbook.js";
import { useCreateTrainingProjectMutation } from "../../../api/entities/training.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";

interface CreateTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateTrainingModal({
  isOpen,
  onClose,
}: CreateTrainingModalProps) {
  const teammate = useAppSelector(selectTeammate);
  if (!teammate) {
    throw new Error("Teammate not found");
  }

  const [createTraining, { isLoading: isCreatingTraining }] =
    useCreateTrainingProjectMutation();

  const { data: playbooks, isLoading: isLoadingPlaybooks } =
    useAllMinimalPlaybooksQuery({
      shouldExcludeTeammateAgentPlaybooks: true,
    });

  const [name, setName] = useState("");
  const [playbookId, setPlaybookId] = useState<PlaybookId | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const playbookOptions: SimpleSelectOption<PlaybookId>[] = (
    playbooks ?? []
  ).map((playbook) => ({
    value: playbook.id,
    label: playbook.name,
  }));

  useEffect(() => {
    if (isOpen) {
      setName("");
      setPlaybookId(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!name.trim() || !playbookId) {
      showNotification(dispatch, {
        id: `validation-error-${Date.now()}`,
        primaryMessage: "Please provide a name and select a playbook.",
        type: NotificationType.FAILURE,
      });
      return;
    }

    try {
      const short_id = nanoid();
      const projectData = await createTraining({
        name: name.trim(),
        short_id,
        owner_teammate_ids: [teammate.id],
        playbook_id: playbookId,
      }).unwrap();

      showNotification(dispatch, {
        id: `training-created-${new Date().getTime().toString()}`,
        primaryMessage: `Training successfully created`,
        type: NotificationType.SUCCESS,
      });

      setName("");
      onClose();
      navigate(
        buildProjectUrlPath(
          projectData.data.project.id,
          ProjectDetailsTabPath.ProjectConfiguration,
        ),
      );
    } catch (err: any) {
      console.error("Error creating training:", err);
      showNotification(dispatch, {
        id: `training-error-${new Date().getTime().toString()}`,
        primaryMessage: `Failed to create training: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <div className="space-y-6">
        <ModalHeader onClose={onClose}>Create training</ModalHeader>

        <div className="space-y-4">
          <div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sales team initial training"
              label="Name"
              autoFocus
              maxLength={100}
            />
          </div>
          <div>
            <SimpleSelect
              options={playbookOptions}
              value={playbookId}
              onChange={setPlaybookId}
              isDisabled={isLoadingPlaybooks}
              placeholder="Select a playbook"
              label="Playbook"
            />
            <div className="mt-2 text-sm text-muted-foreground">
              Assess participants for their ability to succeed in executing
              relevant parts of this playbook.
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onClose}
              isDisabled={isCreatingTraining}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              onClick={handleSubmit}
              isDisabled={isCreatingTraining || !name.trim() || !playbookId}
              isLoading={isCreatingTraining}
            >
              Create training
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
